/**
 * Needs edit: every song someone has flagged, why, who and when, with the way to
 * fix them -- open them in the tag editor, then mark them fixed.
 *
 * The flags are the engine's (library/needs_edit.py), so the list is the same on
 * every machine. The same songs are findable with `needsedit:yes` in any search,
 * and the inbox counts them.
 */

import { useMemo, useState } from "react";
import { useApp } from "../core/app";
import { REASONS, markFixed, reasonLabel, useNeedsEdit } from "../core/needsEdit";
import { songs } from "../format/count";
import { Icon } from "../shell/Icons";

function when(stamp: string): string {
  const d = new Date(stamp);
  return Number.isFinite(d.getTime()) ? d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : stamp;
}

export function NeedsEditView() {
  const app = useApp();
  const flagged = useNeedsEdit();
  const [reason, setReason] = useState<string | null>(app.nav.params.reason ?? null);
  const [picked, setPicked] = useState<Set<string>>(new Set());

  const counts = useMemo(() => {
    const out = new Map<string, number>();
    for (const s of flagged) out.set(s.reason, (out.get(s.reason) ?? 0) + 1);
    return out;
  }, [flagged]);
  const reasons = [...REASONS.map((r) => r.id).filter((id) => counts.has(id)), ...[...counts.keys()].filter((k) => !REASONS.some((r) => r.id === k))];
  const shown = reason ? flagged.filter((s) => s.reason === reason) : flagged;
  const chosen = shown.filter((s) => picked.has(s.trackId));
  const target = chosen.length ? chosen : shown;

  const open = (ids: string[]) => app.navigate("studio", { ids: ids.join(",") });
  const fix = async (ids: string[], title?: string) => {
    if (await markFixed(ids, title)) setPicked((p) => new Set([...p].filter((id) => !ids.includes(id))));
  };

  if (app.offline) return <section className="nv-view"><div className="nv-empty">Start the engine to see the songs that need editing.</div></section>;

  return (
    <section className="nv-view nv-needs" aria-label="Needs edit">
      <div className="nv-viewhead">
        <div className="nv-viewtitle">
          <h1>Needs edit</h1>
          <div className="nv-sub">
            {flagged.length ? `${songs(flagged.length)} flagged` : "Nothing flagged"} · fix them in the tag editor, then mark them fixed
          </div>
        </div>
        {shown.length > 0 && (
          <div className="nv-actions">
            <button className="nv-btn" onClick={() => open(target.map((s) => s.trackId))}>
              <Icon name="edit" /> Open {chosen.length ? songs(chosen.length) : "all"} in the tag editor
            </button>
            <button className="nv-btn" onClick={() => void fix(target.map((s) => s.trackId))}>
              <Icon name="check" /> Mark {chosen.length ? songs(chosen.length) : "all shown"} fixed
            </button>
          </div>
        )}
      </div>
      {reasons.length > 1 && (
        <div className="nv-chips" role="toolbar" aria-label="Reasons">
          <button className={`nv-chip ${reason === null ? "on" : ""}`} onClick={() => setReason(null)}>All {flagged.length}</button>
          {reasons.map((r) => (
            <button key={r} className={`nv-chip ${reason === r ? "on" : ""}`} onClick={() => setReason(reason === r ? null : r)}>
              {reasonLabel(r)} {counts.get(r)}
            </button>
          ))}
        </div>
      )}
      {shown.length === 0 ? (
        <div className="nv-empty">
          <Icon name="check" size={22} />
          <b>Nothing needs editing</b>
          <span>When someone flags a song (its ⋯ menu → Flag for editing, or Radio's Dirty button) it shows here, and in the inbox.</span>
        </div>
      ) : (
        <div className="nv-needs-scroll">
          <table className="nv-table nv-needs-table">
            <thead>
              <tr>
                <th className="pick">
                  <input
                    type="checkbox"
                    aria-label="Select all"
                    checked={chosen.length === shown.length}
                    onChange={(e) => setPicked(e.target.checked ? new Set(shown.map((s) => s.trackId)) : new Set())}
                  />
                </th>
                <th>Song</th>
                <th className="why">Why</th>
                <th className="note">Note</th>
                <th className="when">Flagged</th>
                <th className="nv-needs-row-actions" aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {shown.map((s) => (
                <tr key={s.trackId} className={picked.has(s.trackId) ? "on" : ""}>
                  <td className="pick">
                    <input
                      type="checkbox"
                      aria-label={`Select “${s.title}”`}
                      checked={picked.has(s.trackId)}
                      onChange={(e) => setPicked((p) => {
                        const next = new Set(p);
                        if (e.target.checked) next.add(s.trackId);
                        else next.delete(s.trackId);
                        return next;
                      })}
                    />
                  </td>
                  <td>
                    <button className="nv-link" onClick={() => open([s.trackId])} title="Open in the tag editor">
                      <b>{s.title}</b>
                    </button>
                    <span className="nv-dim"> — {s.artist}</span>
                  </td>
                  <td><span className={`nv-flag ${s.reason === "dirty" ? "bad" : ""}`}>{reasonLabel(s.reason)}</span></td>
                  <td className="nv-dim" title={s.note ?? undefined}>{s.note ?? ""}</td>
                  <td className="nv-dim" title={`${when(s.at)}${s.by ? ` by ${s.by}` : ""}`}>{when(s.at)}{s.by ? ` · ${s.by}` : ""}</td>
                  <td className="nv-needs-row-actions">
                    <button className="nv-btn sm" onClick={() => open([s.trackId])}>Edit</button>
                    <button className="nv-btn sm" onClick={() => void fix([s.trackId], s.title)}>Fixed</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
